import React from 'react';

import { gql } from '@apollo/client';
import { Typography } from '@mui/material';
import { PermissionRoleMenu } from 'src/components';
import { ToggleSwitch } from 'src/components/generic';
import { Apollo } from 'src/gql/generated';
import { getPermissionRoleText } from 'src/helpers';
import { useMostPermissiveUserStorableRole } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

void gql`
  mutation UpdateDocumentForwarding($input: UpdateDocumentInput!) {
    updateDocumentProps(input: $input) {
      document {
        __typename
        id
        forwardingPermissionRole
      }
    }
  }
`;

const useStyles = makeStyles()(theme => ({
  forwardingDetails: { display: 'flex', flexDirection: 'column' },
  permissionRoleMenu: {
    height: 32,
    marginLeft: theme.spacing(1),
    width: 'fit-content',
  },
  row: {
    alignItems: 'center',
    display: 'flex',
  },
  text: {
    marginLeft: theme.spacing(2),
  },
}));

interface DocumentForwardingToggleProps {
  document: Pick<Apollo.Document, 'id' | 'forwardingPermissionRole' | '__typename'>;
}

export function DocumentForwardingToggle({ document }: DocumentForwardingToggleProps): JSX.Element {
  const { classes } = useStyles();
  const [updateDocument] = Apollo.useUpdateDocumentForwardingMutation();
  const { isEditor, isOwner } = useMostPermissiveUserStorableRole(document.id);
  const isPermittedToEdit = isOwner || isEditor;

  async function handleToggle(forwardingPermissionRole: Apollo.PermissionRole | null) {
    await updateDocument({
      optimisticResponse: {
        __typename: 'Mutation',
        updateDocumentProps: {
          __typename: 'DocumentResponse',
          document: {
            __typename: 'Document',
            forwardingPermissionRole,
            id: document.id,
          },
        },
      },
      variables: {
        input: {
          documentId: document.id,
          documentProps: { forwardingPermissionRole },
        },
      },
    });
  }

  return (
    <div className={classes.row}>
      <ToggleSwitch
        disabled={!isPermittedToEdit}
        toggle={async () =>
          await handleToggle(
            !document.forwardingPermissionRole ? Apollo.PermissionRole.Commenter : null
          )
        }
        toggled={Boolean(document.forwardingPermissionRole)}
      />
      <div className={classes.forwardingDetails}>
        <PermissionRoleMenu
          MenuProps={{
            anchorOrigin: { horizontal: 'left', vertical: 'top' },
            transformOrigin: { horizontal: 'left', vertical: 'top' },
            variant: 'menu',
          }}
          border={false}
          className={classes.permissionRoleMenu}
          disabled={!document.forwardingPermissionRole || !isPermittedToEdit}
          handleSelect={async role => await handleToggle(role)}
          value={document.forwardingPermissionRole}
        />
        <Typography className={classes.text}>
          {document.forwardingPermissionRole
            ? `Document forwarding enabled. Recipients can ${getPermissionRoleText(
                document.forwardingPermissionRole
              ).toLowerCase()}.`
            : `Document forwarding disabled.`}
        </Typography>
      </div>
    </div>
  );
}
